/** Profile, language, data rights (PDPA), legal, paywall. */
import { config } from '../../data/config';
import { repo } from '../../data/repo';
import { newId } from '../../data/localStore';
import { DAY_MS, pregnancyInfo, startOfLocalDay, validateDueDate } from '../../domain/pregnancy';
import type { CountSession, Kick, Locale, Plan, Zone } from '../../domain/types';
import { fmtDate, LANGS, setLocale, t } from '../../i18n';
import { sfx } from '../../audio/sfx';
import { $, closeSheet, confirmSheet, esc, openSheet, toast } from '../dom';
import { kickSummary } from '../../domain/stats';
import { hasPremium } from '../../domain/types';
import { bumpSvg, cat, filmStrip, owl, rooster } from '../art';
import { go, register, type Screen } from '../router';

const VERSION = '0.3.0';

function planLabel(): string {
  const e = repo.entitlement;
  if (!e) return '–';
  if (e.status === 'trial') return t('profile', 'trialLeft', { n: Math.max(0, Math.ceil(((e.trialEndsAt ?? 0) - repo.now()) / DAY_MS)) });
  if (e.status === 'paid') return e.plan === 'lifetime' || !e.paidUntil || !isFinite(e.paidUntil) ? t('profile', 'premiumForever') : t('profile', 'premiumUntil', { d: fmtDate(e.paidUntil) });
  return t('profile', 'free');
}

export function showLegal(doc: 'terms' | 'privacy'): void {
  openSheet(`<h3>${esc(t('profile', doc))}</h3><p class="small" style="color:var(--amber);margin-top:6px">${esc(t('legal', 'draft'))}</p>
    <p class="legal-text">${esc(t('legal', doc))}</p><button class="btn btn-ghost btn-block" data-action="sheet-close">${esc(t('common', 'close'))}</button>`);
}

function row(label: string, value: string, action: string, field?: string, cls = ''): string {
  return `<button class="list-row ${cls}" data-action="${action}" ${field ? `data-field="${field}"` : ''}><span class="k">${esc(label)}</span><span class="v">${value}<span class="chev">›</span></span></button>`;
}

const profile: Screen = {
  id: 'profile', nav: 'profile', root: true,
  render: () => {
    const p = repo.profile!;
    const preg = pregnancyInfo(p.dueDate);
    const e = repo.entitlement;
    return `
    <div class="home-head"><h2>${esc(t('profile', 'title'))}</h2></div>
    <div class="stack">
      <div class="row" style="padding:4px 0 8px">
        <div class="avatar">${esc(p.displayName.trim().charAt(0) || '♥')}</div>
        <div><div style="font-size:18px;font-weight:700">${esc(p.displayName)}</div>
        <div class="small muted">${esc(t('common', 'week'))} ${preg.week} · ${esc(preg.daysLeft >= 0 ? t('due', 'daysLeft', { n: preg.daysLeft }) : t('due', 'pastDue', { n: -preg.daysLeft }))}</div></div>
      </div>

      ${planCardHtml()}

      <div class="section-title">${esc(t('profile', 'pregnancy'))}</div>
      <div class="card list">
        ${row(t('profile', 'dueDate'), esc(fmtDate(new Date(p.dueDate + 'T00:00:00').getTime())), 'edit', 'due')}
        ${row(t('profile', 'baby'), esc(p.babyNickname || t('profile', 'notSet')), 'edit', 'baby')}
        ${row(t('profile', 'mom'), esc(p.displayName), 'edit', 'mom')}
        ${row(t('profile', 'hospital'), `<span class="num">${esc(p.hospitalPhone || t('profile', 'notSet'))}</span>`, 'edit', 'hospital')}
      </div>

      <div class="section-title">${esc(t('profile', 'app'))}</div>
      <div class="card list">
        ${row(t('profile', 'language'), esc(LANGS.find(l => l.code === (repo.locale ?? 'th'))?.label ?? ''), 'pick-language')}
        <button class="list-row" data-action="toggle-sound"><span class="k">${esc(t('profile', 'sound'))}</span><span class="toggle ${p.soundOn ? 'on' : ''}"></span></button>
      </div>

      ${repo.guest ? `<div class="section-title">${esc(t('profile', 'account'))}</div>
      <div class="card guest-card">
        <p class="small" style="margin:0 0 10px">${esc(t('account', 'guestCardLead'))}</p>
        <button class="btn btn-primary btn-block" data-action="go" data-to="account">${esc(t('account', 'guestCardCta'))}</button>
      </div>` : ''}
      ${repo.user ? `<div class="section-title">${esc(t('profile', 'account'))}</div>
      <div class="card list">
        <div class="list-row"><span class="k">${esc(t('profile', 'signedInAs'))}</span><span class="v small">${esc(repo.user.email ?? '')}</span></div>
        <button class="list-row" data-action="sign-out"><span class="k">${esc(t('profile', 'signOut'))}</span><span class="chev">›</span></button>
      </div>` : ''}

      <div class="section-title">${esc(t('profile', 'data'))}</div>
      <div class="card list">
        <button class="list-row" data-action="export"><span class="k">${esc(t('profile', 'export'))}</span><span class="chev">›</span></button>
        <button class="list-row danger" data-action="delete-account"><span class="k">${esc(t('profile', 'delete'))}</span><span class="chev">›</span></button>
      </div>

      <div class="section-title">${esc(t('profile', 'about'))}</div>
      <div class="card list">
        <button class="list-row" data-action="legal" data-doc="terms"><span class="k">${esc(t('profile', 'terms'))}</span><span class="chev">›</span></button>
        <button class="list-row" data-action="legal" data-doc="privacy"><span class="k">${esc(t('profile', 'privacy'))}</span><span class="chev">›</span></button>
        <div class="list-row"><span class="k small">${esc(t('profile', 'notMedical'))}</span></div>
      </div>

      ${repo.demo ? `<div class="section-title">${esc(t('profile', 'demoTools'))}</div>
      <div class="card list">
        <button class="list-row" data-action="demo-seed"><span class="k">${esc(t('profile', 'seed'))}</span><span class="chev">›</span></button>
        <button class="list-row" data-action="demo-expire"><span class="k">${esc(t('profile', 'expire'))}</span><span class="chev">›</span></button>
      </div>` : ''}
      <p class="small muted" style="text-align:center;padding:8px 0">${esc(t('profile', 'version', { v: VERSION }))}</p>
    </div>`;
  },
  refresh() { go('profile', { replace: true }); },
  actions: {
    edit: el => editField(el.dataset.field as 'due' | 'baby' | 'mom' | 'hospital'),
    'pick-language': () => openSheet(`<h3 style="margin-bottom:12px">${esc(t('profile', 'language'))}</h3><div class="lang-list" style="max-width:none">
      ${LANGS.map(l => `<button class="lang-btn" data-action="set-lang" data-lang="${l.code}" ${l.available ? '' : 'disabled'}><span>${esc(l.label)}</span>${l.available ? (l.code === repo.locale ? '✓' : '') : `<small>${esc(t('lang', 'soon'))}</small>`}</button>`).join('')}</div>`,
      { 'set-lang': b => { const l = b.dataset.lang as Locale; setLocale(l); repo.setLocale(l); closeSheet(); go('profile', { replace: true }); } }),
    'toggle-sound': () => {
      const p = repo.profile!; const on = !p.soundOn;
      repo.saveProfile({ ...p, soundOn: on }); sfx.setEnabled(on); if (on) sfx.tap();
      go('profile', { replace: true });
    },
    'sign-out': () => confirmSheet(t('profile', 'signOut'), t('profile', 'signOutLead'), t('profile', 'signOut'), t('common', 'cancel'), async () => {
      await repo.signOut(); go('start', { replace: true });
    }),
    export: async () => {
      try {
        const data = await repo.exportData();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `bumpkick-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      } catch { toast(t('common', 'error')); }
    },
    'delete-account': () => confirmSheet(t('profile', 'delete'), t('profile', 'deleteLead'), t('profile', 'deleteOk'), t('common', 'cancel'), async () => {
      try { await repo.deleteAccount(); toast(t('profile', 'deleted')); go('start', { replace: true }); }
      catch { toast(t('common', 'error')); }
    }, true),
    legal: el => showLegal(el.dataset.doc as 'terms' | 'privacy'),
    'demo-seed': () => { seedDemo(); toast(t('common', 'saved')); },
    'demo-expire': () => { repo.demo?.expireTrial(); go('home', { replace: true }); }
  }
};

function editField(field: 'due' | 'baby' | 'mom' | 'hospital') {
  const p = repo.profile!;
  const cfg = {
    due: { title: t('profile', 'dueDate'), input: `<input type="date" id="edit-val" value="${esc(p.dueDate)}">` },
    baby: { title: t('profile', 'baby'), input: `<input type="text" id="edit-val" maxlength="30" value="${esc(p.babyNickname ?? '')}" placeholder="${esc(t('names', 'babyPh'))}">` },
    mom: { title: t('profile', 'mom'), input: `<input type="text" id="edit-val" maxlength="40" value="${esc(p.displayName)}">` },
    hospital: { title: t('hospital', 'title'), input: `<input type="tel" id="edit-val" inputmode="tel" maxlength="20" value="${esc(p.hospitalPhone ?? '')}" placeholder="${esc(t('hospital', 'ph'))}">` }
  }[field];
  openSheet(`<h3 style="margin-bottom:12px">${esc(cfg.title)}</h3><div class="field">${cfg.input}<span class="error" id="edit-err" hidden></span></div>
    <div class="stack" style="margin-top:16px"><button class="btn btn-primary btn-block" data-action="edit-save">${esc(t('common', 'save'))}</button>
    <button class="btn btn-ghost btn-block" data-action="sheet-close">${esc(t('common', 'cancel'))}</button></div>`, {
    'edit-save': () => {
      const v = ($('#edit-val') as HTMLInputElement).value.trim();
      const err = (msg: string) => { const e = $('#edit-err')!; e.textContent = msg; e.hidden = false; };
      if (field === 'due') {
        const bad = validateDueDate(v);
        if (bad) return err(bad === 'too_far' ? t('due', 'errTooFar') : t('due', 'errTooPast'));
        repo.saveProfile({ ...p, dueDate: v });
      }
      if (field === 'mom') { if (!v) return err(t('names', 'mom')); repo.saveProfile({ ...p, displayName: v }); }
      if (field === 'baby') repo.saveProfile({ ...p, babyNickname: v || null });
      if (field === 'hospital') {
        if (v && !/^[0-9+() -]{3,20}$/.test(v)) return err(t('hospital', 'invalid'));
        repo.saveProfile({ ...p, hospitalPhone: v || null });
      }
      closeSheet(); toast(t('common', 'saved')); go('profile', { replace: true });
    }
  });
}

/** Demo mode only: realistic week of data to try the stats screens. */
function seedDemo() {
  if (!repo.demo) return;
  const kicks: Kick[] = [];
  const sessions: CountSession[] = [];
  const today = startOfLocalDay(Date.now());
  const zones: Zone[] = ['lower_right', 'lower_right', 'lower_left', 'upper_right', 'lower_right', 'upper_left'];
  for (let d = 6; d >= 1; d--) {
    for (const hour of [10, 20.5]) {
      const start = today - d * DAY_MS + hour * 3_600_000 + Math.random() * 1_800_000;
      const n = d === 3 && hour === 10 ? 6 : 10 + Math.floor(Math.random() * 3);
      const sid = newId();
      for (let i = 0; i < n; i++) {
        const z = zones[Math.floor(Math.random() * zones.length)]!;
        kicks.push({ clientId: newId(), occurredAt: start + i * (180_000 + Math.random() * 240_000), source: 'session', sessionClientId: sid, zone: z, x: z.endsWith('left') ? -0.4 : 0.4, y: z.startsWith('upper') ? 0.2 : -0.9, z: 1.3 });
      }
      sessions.push({ clientId: sid, startedAt: start, endedAt: start + n * 300_000, kickCount: n, reachedTarget: n >= 10, lowMovementFlag: false, note: null });
    }
  }
  repo.demo.seed(kicks, sessions);
}

// ================================================================== PAYWALL
const plan: Plan = 'lifetime';

/** Glows for the bump preview: her own kicks if we have them, otherwise a labelled sample. */
function previewGlows(): { glows: { x: number; y: number; warm: boolean; strength: number }[]; sample: boolean } {
  const sum = kickSummary(repo.kicks);
  if (sum.spots.length >= 3) return { glows: sum.spots.map(p => ({ ...p, warm: p.y < 0.1, strength: 0.22 })), sample: false };
  const sample = [[0.45, -0.45], [0.55, -0.35], [0.38, -0.58], [0.5, -0.52], [-0.4, 0.42], [-0.32, 0.5], [0.6, -0.42]];
  return { glows: sample.map(([x, y]) => ({ x: x!, y: y!, warm: y! < 0.1, strength: 0.3 })), sample: true };
}

function planCardHtml(): string {
  const e = repo.entitlement;
  const paid = e?.status === 'paid';
  const { glows } = previewGlows();
  return `<button class="plan-card ${paid ? 'paid' : ''}" ${paid ? '' : 'data-action="go" data-to="paywall"'}>
    <div class="pc-bump">${bumpSvg({ glows, width: 76 })}</div>
    <div class="pc-txt"><div class="pc-k">${esc(paid ? t('paywall', 'yours') : t('profile', 'plan'))}</div>
      <div class="pc-v">${esc(planLabel())}</div>
      ${paid ? '' : `<div class="pc-cta">${esc(t('paywall', 'seeAll'))} ›</div>`}</div>
  </button>`;
}

type SampleKind = 'story' | 'timelapse' | 'personality';

/** Real examples of what Premium makes (made with sample data, labelled as such). Videos load only when opened. */
function showSample(kind: SampleKind): void {
  const label = `<p class="sample-label">${esc(t('paywall', 'sampleNote'))}</p>`;
  if (kind === 'personality') {
    const card = (cls: string, art: string, k: 'pOwl' | 'pRooster' | 'pCat', q: 'pOwlQ' | 'pRoosterQ' | 'pCatQ') =>
      `<div class="pcard ${cls}"><div class="pcard-art">${art}</div><div><div class="pcard-t">${esc(t('paywall', k))}</div><div class="pcard-q">${esc(t('paywall', q))}</div></div></div>`;
    openSheet(`<h3 style="margin:0 0 4px">${esc(t('paywall', 'cardOwl'))}</h3>${label}
      <div class="stack">${card('night', owl(64), 'pOwl', 'pOwlQ')}${card('warm', rooster(64), 'pRooster', 'pRoosterQ')}${card('mint', cat(64), 'pCat', 'pCatQ')}</div>
      <button class="btn btn-ghost btn-block" data-action="sheet-close" style="margin-top:12px">${esc(t('common', 'close'))}</button>`, {}, { className: 'sample-sheet' });
    return;
  }
  const src = kind === 'story' ? '/samples/story' : '/samples/timelapse';
  openSheet(`<h3 style="margin:0 0 4px">${esc(t('paywall', kind === 'story' ? 'cardFilm' : 'cardTimelapse'))}</h3>${label}
    <video class="sample-video" src="${src}.mp4" poster="${src}.jpg" autoplay muted loop playsinline controls preload="none"></video>
    <button class="btn btn-ghost btn-block" data-action="sheet-close" style="margin-top:10px">${esc(t('common', 'close'))}</button>`, {}, { className: 'sample-sheet' });
}

const paywall: Screen = {
  id: 'paywall',
  render: () => {
    const e = repo.entitlement;
    const trial = e?.status === 'trial';
    const left = trial ? Math.max(0, Math.ceil(((e!.trialEndsAt ?? 0) - repo.now()) / DAY_MS)) : 0;
    const sum = kickSummary(repo.kicks);
    const baby = repo.profile?.babyNickname || t('paywall', 'babyFallback');
    const { glows, sample } = previewGlows();
    const premium = hasPremium(e);
    const letter = t('paywall', 'letter');
    const tick = '<span class="ok">✓</span>';
    return `
    <div class="ob-top"><button class="back-btn" data-action="back" aria-label="${esc(t('common', 'close'))}">✕</button><span></span><span style="width:40px"></span></div>
    <div class="stack" style="flex:1">
      <div class="pw-hero">
        <h2>${esc(sum.total >= 5 ? t('paywall', 'heroCount', { baby, n: sum.total }) : t('paywall', 'heroDefault'))}</h2>
        <p class="muted small">${esc(t('paywall', 'heroSub', { baby }))}</p>
        ${trial ? `<p class="small" style="color:var(--rose-dark);margin-top:4px">${esc(t('paywall', 'trialLead', { n: left }))}</p>` : ''}
      </div>
      <div class="card pw-preview">
        <div class="pw-bump">${bumpSvg({ glows, width: 210 })}
          <span class="chip-s">${esc(sample ? t('paywall', 'sample') : premium ? '💛' : '🔒 Premium')}</span></div>
        ${!sample && (sum.topZone || sum.topHour !== null) ? `<p class="pw-stat">
          ${sum.topZone ? `${esc(t('paywall', 'favSpot'))}: <b>${esc(t('zones', sum.topZone))}</b>` : ''}
          ${sum.topZone && sum.topHour !== null ? ' · ' : ''}
          ${sum.topHour !== null ? `${esc(t('paywall', 'favHour'))} <b>${esc(t('paywall', 'hourFmt', { h: sum.topHour }))}</b>` : ''}</p>` : ''}
        <p class="pw-try">${esc(t('paywall', 'trySamples'))}</p>
        <div class="pw-samples">
          <button class="pws lilac" data-action="pw-sample" data-kind="story"><span class="chip-s">▶ ${esc(t('paywall', 'sample'))}</span>${filmStrip(40)}<span>${esc(t('paywall', 'cardFilm'))}</span></button>
          <button class="pws warm" data-action="pw-sample" data-kind="timelapse"><span class="chip-s">▶ ${esc(t('paywall', 'sample'))}</span>${bumpSvg({ glows: [{ x: 0.4, y: -0.45, warm: true, strength: 0.6 }, { x: -0.35, y: 0.4, warm: false, strength: 0.5 }], foot: { x: 0.4, y: -0.45 }, width: 40 })}<span>${esc(t('paywall', 'cardTimelapse'))}</span></button>
          <button class="pws night" data-action="pw-sample" data-kind="personality"><span class="chip-s">${esc(t('paywall', 'sample'))}</span>${owl(40)}<span>${esc(t('paywall', 'cardOwl'))}</span></button>
        </div>
      </div>
      <div class="card pw-table"><table>
        <tr><th></th><th>${esc(t('paywall', 'colFree'))}</th><th>${esc(t('paywall', 'colPremium'))}</th></tr>
        <tr><td>${esc(t('paywall', 'rowCount'))}</td><td>${tick}</td><td>${tick}</td></tr>
        <tr><td>${esc(t('paywall', 'rowCall'))}</td><td>${tick}</td><td>${tick}</td></tr>
        <tr><td>${esc(t('paywall', 'rowSpot'))}</td><td></td><td>${tick}</td></tr>
        <tr><td>${esc(t('paywall', 'rowStats'))}</td><td></td><td>${tick}</td></tr>
        <tr><td>${esc(t('paywall', 'rowWrapped'))} <span class="chip-s inline">${esc(t('paywall', 'soonChip'))}</span></td><td></td><td>${tick}</td></tr>
      </table></div>
      <div class="plan on" role="group">
        <span style="font-size:26px">🔓</span>
        <span><b>${esc(t('paywall', 'lifetime'))}</b><br><span class="small muted">${esc(t('paywall', 'lifetimeSub'))}</span></span>
        <span class="price"><b>฿${config.price.thb}</b></span>
      </div>
      <p class="small muted" style="text-align:center">${esc(t('paywall', 'noAuto'))}</p>
      ${letter ? `<div class="pw-letter"><b>${esc(t('paywall', 'letterTitle'))} 💌</b><p>${esc(letter)}</p><p class="sign">${esc(t('paywall', 'letterSign'))}</p></div>` : ''}
    </div>
    <div class="stack screen-pad-bottom" style="padding-top:12px">
      ${config.paymentsEnabled && repo.guest
        ? `<button class="btn btn-primary btn-block" data-action="go" data-to="account">${esc(t('paywall', 'signInToBuy'))}</button>`
        : `<button class="btn btn-primary btn-block" data-action="buy" ${config.paymentsEnabled && repo.user ? '' : 'disabled'}>${esc(config.paymentsEnabled ? t('paywall', 'buy') : t('paywall', 'soon'))}</button>`}
      <button class="link-btn" data-action="back">${esc(trial ? t('paywall', 'later') : t('paywall', 'continueFree'))}</button>
    </div>`;
  },
  actions: {
    'pw-sample': el => showSample(el.dataset.kind as SampleKind),
    buy: async el => {
      (el as HTMLButtonElement).disabled = true;
      try { location.href = await repo.startCheckout(plan); }
      catch { toast(t('common', 'error')); (el as HTMLButtonElement).disabled = false; }
    }
  }
};

/** Back from Stripe Checkout: wait for the webhook to grant access (server decides, not the URL). */
export async function handleCheckoutReturn(): Promise<void> {
  const q = new URLSearchParams(location.search);
  const status = q.get('checkout');
  if (!status) return;
  history.replaceState(history.state, '', location.pathname);
  if (status !== 'success') return;
  toast(t('paywall', 'pending'));
  for (let i = 0; i < 10; i++) {
    await repo.refreshEntitlement();
    if (repo.entitlement?.status === 'paid') { sfx.complete(); toast(t('paywall', 'thanks')); return; }
    await new Promise(r => setTimeout(r, 2000));
  }
}

register(profile, paywall);
